import type {
  DesignDocument,
  DesignObject,
  EditorState,
  ProductConfig,
  ProductViewId,
  PrintSurfaceDesign,
} from './types';
import { DEFAULT_VIEWPORT, createId } from './types';
import { createDefaultSurfacePrintEffects } from './print-effects';
import { designDocumentSchema } from './schemas';
import { resolveDefaultSize, resolvePrintableArea, resolveProductColour } from '../products';

const now = () => new Date().toISOString();

/** Hidden and fully transparent layers do not create printable artwork. */
export function isVisibleArtworkObject(object: DesignObject): boolean {
  return object.visible && object.opacity > 0;
}

export function createEmptyDesign(product: ProductConfig): DesignDocument {
  const firstVariant = product.variants[0];
  if (!firstVariant) throw new Error('Product needs a variant');
  const defaultColour = resolveProductColour(product, product.defaultColourId, firstVariant.id);
  const defaultSize = resolveDefaultSize(firstVariant);

  const surfaces = Object.fromEntries(
    product.printableAreas.map(baseArea => {
      const area = resolvePrintableArea(product, firstVariant.id, defaultSize, baseArea.viewId);
      return [
        area.viewId,
        {
          viewId: area.viewId,
          printAreaMm: structuredClone(area.sizeMm),
          objects: [],
          printEffects: createDefaultSurfacePrintEffects(),
        } satisfies PrintSurfaceDesign,
      ];
    }),
  ) as Partial<Record<ProductViewId, PrintSurfaceDesign>>;

  return {
    schemaVersion: 1,
    id: createId('design'),
    productId: product.id,
    variantId: firstVariant.id,
    size: defaultSize,
    quantity: 1,
    colourId: defaultColour.id,
    technology: product.supportedTechnologies[0] ?? 'dtf',
    surfaces,
    assets: {},
    acknowledgedWarnings: [],
    createdAt: now(),
    updatedAt: now(),
  };
}

export function createInitialEditorState(product: ProductConfig): EditorState {
  const activeViewId = product.printableAreas[0]?.viewId ?? 'front';
  return {
    design: createEmptyDesign(product),
    activeViewId,
    selectedObjectIds: [],
    viewports: {
      front: structuredClone(DEFAULT_VIEWPORT),
      back: structuredClone(DEFAULT_VIEWPORT),
      'left-sleeve': structuredClone(DEFAULT_VIEWPORT),
      'right-sleeve': structuredClone(DEFAULT_VIEWPORT),
    },
  };
}

export function serializeDesign(design: DesignDocument): string {
  return JSON.stringify(design);
}

export function deserializeDesign(json: string): DesignDocument {
  const parsed = JSON.parse(json) as { schemaVersion?: unknown };
  if (parsed.schemaVersion !== 1)
    throw new Error(`Unsupported design schema ${String(parsed.schemaVersion)}`);
  return designDocumentSchema.parse(parsed) as DesignDocument;
}

export function migrateDesign(input: unknown): DesignDocument {
  const candidate = input as { schemaVersion?: unknown };
  if (candidate.schemaVersion === 1) {
    return designDocumentSchema.parse(structuredClone(input)) as DesignDocument;
  }
  throw new Error('No migration path exists for this design schema');
}
